// نمایش مبلغ، تاریخ شمسی، وضعیت و نشان‌ها (UpgradeBadge، SampleBadge، AccessModeBanner).
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useTranslation } from '@phoenix/i18n';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { formatMoney, type MoneyDisplayUnit } from '../format/money';
import { DEFAULT_TIME_ZONE, toJalali, toJalaliDateTime } from '../format/jalali';
import { statusColors, type StatusTone } from '../theme/tokens';

export interface DisplaySettings {
  /** plt.display_currency_unit (US-PLT-071)؛ پیش‌فرض Rial. */
  currencyUnit: MoneyDisplayUnit;
  /** plt.default_timezone (ADR-014)؛ پیش‌فرض Asia/Tehran. */
  timeZone: string;
}

const DisplaySettingsContext = createContext<DisplaySettings>({
  currencyUnit: 'Rial',
  timeZone: DEFAULT_TIME_ZONE,
});

export function DisplaySettingsProvider({
  value,
  children,
}: {
  value: DisplaySettings;
  children: ReactNode;
}) {
  return <DisplaySettingsContext.Provider value={value}>{children}</DisplaySettingsContext.Provider>;
}

export function useDisplaySettings(): DisplaySettings {
  return useContext(DisplaySettingsContext);
}

/** قالب‌بند مبلغ با برچسب‌های ترجمه‌شده و واحد نمایش مستأجر. */
export function useMoneyFormatter() {
  const { t } = useTranslation('common');
  const { currencyUnit } = useDisplaySettings();
  return useMemo(() => {
    const labels = { rial: t('common:money.rial'), toman: t('common:money.toman') };
    return (amountMinor: number | bigint | string, currency = 'IRR', unit: MoneyDisplayUnit = currencyUnit) =>
      formatMoney(amountMinor, currency, unit, { labels });
  }, [t, currencyUnit]);
}

export interface MoneyProps {
  amountMinor: number | bigint | string;
  currency?: string;
  /** واحد نمایش؛ پیش‌فرض تنظیم مستأجر. */
  unit?: MoneyDisplayUnit;
}

/** مبلغ با واحد نمایش مستأجر، جداکننده هزار و ارقام فارسی (ADR-013). */
export function Money({ amountMinor, currency = 'IRR', unit }: MoneyProps) {
  const format = useMoneyFormatter();
  return (
    <bdi data-testid="money" style={{ whiteSpace: 'nowrap' }}>
      {format(amountMinor, currency, unit)}
    </bdi>
  );
}

export interface JalaliDateProps {
  value: Date | string | null | undefined;
  withTime?: boolean;
}

/** تاریخ شمسی در منطقه زمانی مستأجر (ADR-014). */
export function JalaliDate({ value, withTime = false }: JalaliDateProps) {
  const { timeZone } = useDisplaySettings();
  const text = withTime ? toJalaliDateTime(value, { timeZone }) : toJalali(value, { timeZone });
  const iso = value instanceof Date ? value.toISOString() : (value ?? undefined);
  return <time dateTime={iso ?? undefined}>{text}</time>;
}

export interface StatusChipProps {
  tone: StatusTone;
  /** کلید i18n برچسب (پیش‌فرض common:status.{tone}). */
  labelKey?: string;
}

/** وضعیت با رنگ ثابت Design System. */
export function StatusChip({ tone, labelKey }: StatusChipProps) {
  const { t } = useTranslation('common');
  const color = statusColors[tone];
  return (
    <Chip
      label={t(labelKey ?? `common:status.${tone}`)}
      data-status={tone}
      sx={{ color, bgcolor: alpha(color, 0.12), fontWeight: 500, border: `1px solid ${alpha(color, 0.3)}` }}
    />
  );
}

export interface UpgradeBadgeProps {
  /** کلید Feature غیرفعال (feature.{ctx}.{name}). */
  feature?: string;
  /** صفحه اشتراک (SCR-STAFF-904). */
  to?: string;
}

/** نشان «نیازمند ارتقا» با لینک به اشتراک (AC-PLT-031.2). */
export function UpgradeBadge({ feature, to }: UpgradeBadgeProps) {
  const { t } = useTranslation('common');
  const chip = (
    <Chip
      icon={<RocketLaunchOutlinedIcon />}
      label={t('common:upgrade.badge')}
      color="secondary"
      variant="outlined"
      data-testid="upgrade-badge"
      data-feature={feature}
      {...(to ? { component: RouterLink, to, clickable: true } : {})}
    />
  );
  return <Tooltip title={t('common:upgrade.tooltip')}>{chip}</Tooltip>;
}

/** نشان «نمونه» برای محتوای IsSample (CLAUDE.md §10: هرگز بدون نشان نمایش داده نمی‌شود). */
export function SampleBadge() {
  const { t } = useTranslation('common');
  return (
    <Tooltip title={t('common:sample.tooltip')}>
      <Chip
        icon={<ScienceOutlinedIcon />}
        label={t('common:sample.badge')}
        color="warning"
        variant="outlined"
        data-testid="sample-badge"
      />
    </Tooltip>
  );
}

/**
 * نوار «محتوای نمونه — پیش از استفاده بالینی یا حقوقی بازبینی شود» در صفحات ویرایش و استفاده هر قلم IsSample (AC-CFG-035.1؛ CLAUDE.md §10).
 */
export function SampleContentBanner({ children }: { children?: ReactNode }) {
  const { t } = useTranslation('common');
  return (
    <Alert severity="warning" icon={<ScienceOutlinedIcon />} data-testid="sample-content-banner">
      <Box component="span" sx={{ fontWeight: 600 }}>
        {t('common:sample.banner')}
      </Box>
      {children ? <Box sx={{ mt: 0.5 }}>{children}</Box> : null}
    </Alert>
  );
}

export interface AccessModeBannerProps {
  /** حالت دسترسی مستأجر (Full، ReadOnly، Locked). */
  accessMode: string;
  /** مسیر صفحه اشتراک. */
  subscriptionPath?: string;
}

/** نوار هشدار دائمی وقتی حالت دسترسی Full نیست (AC-PLT-004.2). */
export function AccessModeBanner({ accessMode, subscriptionPath }: AccessModeBannerProps) {
  const { t } = useTranslation('common');
  if (accessMode === 'Full') {
    return null;
  }
  const mode = accessMode === 'Locked' ? 'locked' : 'read_only';
  return (
    <Alert
      severity={mode === 'locked' ? 'error' : 'warning'}
      icon={<LockOutlinedIcon />}
      role="status"
      data-testid="access-mode-banner"
      data-access-mode={accessMode}
      sx={{ borderRadius: 0 }}
      action={
        subscriptionPath ? (
          <Button component={RouterLink} to={subscriptionPath} color="inherit">
            {t('common:access_mode.action')}
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{t(`common:access_mode.${mode}.title`)}</AlertTitle>
      {t(`common:access_mode.${mode}.description`)}
    </Alert>
  );
}

export interface KeyValueItem {
  /** کلید i18n برچسب. */
  labelKey: string;
  value: ReactNode;
  /** شناسه تست مقدار. */
  testId?: string;
}

/** فهرست برچسب و مقدار (dl) برای خلاصه صفحات جزئیات؛ مقدار خالی ← «—». */
export function KeyValueList({
  items,
  columns = 2,
}: {
  items: readonly KeyValueItem[];
  columns?: 1 | 2 | 3;
}) {
  const { t } = useTranslation('common');
  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: `repeat(${columns}, minmax(0, 1fr))` },
        columnGap: 3,
        rowGap: 1.5,
        m: 0,
      }}
    >
      {items.map((item) => (
        <Box key={item.labelKey} sx={{ minWidth: 0 }}>
          <Typography component="dt" variant="caption" color="text.secondary">
            {t(item.labelKey)}
          </Typography>
          <Typography component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }} data-testid={item.testId}>
            {item.value === null || item.value === undefined || item.value === ''
              ? t('common:states.none')
              : item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
